import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { AlertTriangle, Loader2, MailWarning } from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { api, formatDate } from "@/lib/api";
import { ADMIN_NAV } from "./nav";

const TILES = [
  ["total_partners", "Total partners"],
  ["total_families", "Total families"],
  ["total_students", "Total students"],
  ["new_enrollments", "New enrollments (unpaid)"],
  ["records_submitted", "Records submitted"],
  ["records_needing_review", "Records needing review"],
  ["records_needing_correction", "Needing correction"],
  ["records_approved", "Approved records"],
  ["documents_issued", "Documents issued"],
  ["documents_issued_not_sent", "Issued – Not Sent"],
];

export default function AdminDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/dashboard").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  return (
    <AppShell nav={ADMIN_NAV} title="Dashboard" subtitle="Alabama Church School — Partner Portal">
      {!data ? (
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      ) : (
        <div className="space-y-8">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3">
            {TILES.map(([key, label], i) => (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
              >
                <Card
                  className={`border-slate-200 transition-shadow duration-200 hover:shadow-md ${
                    key === "documents_issued_not_sent" && data[key] > 0
                      ? "border-amber-300 bg-amber-50"
                      : ""
                  }`}
                >
                  <CardContent className="p-6">
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {label}
                    </div>
                    <div
                      className="mt-2 font-display text-3xl font-bold text-slate-900"
                      data-testid={`stat-${key}`}
                    >
                      {data[key]}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {data.documents_issued_not_sent > 0 && (
            <Link to="/admin/records?delivery=pending" data-testid="issued-not-sent-link">
              <div className="flex items-center gap-3 rounded-md border border-amber-300 bg-amber-50 px-5 py-4 text-sm text-amber-900 transition-colors hover:bg-amber-100">
                <MailWarning className="h-4 w-4" />
                {data.documents_issued_not_sent} issued document(s) have not been emailed to the
                parent/guardian yet — click to open and send.
              </div>
            </Link>
          )}

          {data.records_needing_review > 0 && (
            <Link to="/admin/records" data-testid="review-queue-link">
              <div className="flex items-center gap-3 rounded-md border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-800 transition-colors hover:bg-amber-100">
                <AlertTriangle className="h-4 w-4" />
                {data.records_needing_review} record(s) are waiting for ACS review.
              </div>
            </Link>
          )}

          <Card className="border-slate-200">
            <CardContent className="p-6">
              <h2 className="text-base font-semibold text-slate-900">Recent activity</h2>
              <ul className="mt-4 divide-y divide-slate-100" data-testid="recent-activity">
                {data.recent_activity.length === 0 && (
                  <li className="py-3 text-sm text-slate-500">No activity yet.</li>
                )}
                {data.recent_activity.map((item) => (
                  <li key={item.id} className="flex flex-wrap justify-between gap-2 py-3 text-sm">
                    <span className="text-slate-800">{item.message}</span>
                    <span className="text-xs text-slate-400">{formatDate(item.at)}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      )}
    </AppShell>
  );
}
